import { type FC, useCallback, useEffect, useState } from 'react';
import RegisterLayout from '@/registration/component/Register/Layout';
import EmailLayout from './EmailLayout';
import LoginDrawer from './PasscodeDrawer';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useSearchParams } from 'react-router-dom';
import { useKitchenSink } from '@/app/ProviderKitchenSink';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { type AuthObject, type LoginRequest, login } from '@/api/registration';
import { useTenantApi } from '@/account/hook/useTenantApi';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { UiStack, uiStyles } from '@/lib/ui';
import GroupHeader from '@/registration/component/Register/Layout/GroupHeader';

const genericErrorMessage = 'Login from email link failed, please sign in using your email below.';

const EmailPage: FC = () => {
  const { userSession, setUserSession } = useUserSession();
  const { reportToGlobal } = useApiErrorHandler();
  const [email, setEmail] = useState<string>();
  const [searchParams] = useSearchParams();
  const [groupRegistrationEnabled, setGroupRegistrationEnabled] = useState<boolean>(false);
  const { setAttendeeCategoryId } = useKitchenSink();
  const token = searchParams.get('access-token');
  const client = searchParams.get('client');
  const uid = searchParams.get('uid');
  const attendeeCategoryId = searchParams.get('attendeeCategoryId');
  const { createTenantApiRequest } = useTenantApi();
  const [error, setError] = useState<string>();
  const queryClient = useQueryClient();
  const toggleGroupRego = useCallback(() => {
    setGroupRegistrationEnabled(!groupRegistrationEnabled);
  }, [
    groupRegistrationEnabled,
  ]);
  const { data, mutate } = useMutation<ApiResponseSingle<AuthObject>, Error, LoginRequest>({
    mutationFn: async (loginData: LoginRequest) => {
      return await login(createTenantApiRequest)(loginData);
    },
    onError: (loginError: Error) => {
      setUserSession(undefined);
      setError(genericErrorMessage);
      reportToGlobal(loginError);
    }
  });

  /**
   * @todo This line can potentially be dangerous. If there is any query above here that will need to fetch some data before rendering the children,
   *        and if isFetching is being watched, this could potentially create an inifite reloading of the page.
   */
  useEffect(() => {
    queryClient.invalidateQueries();
  }, []);

  useEffect(() => {
    if (token && client && uid) {
      mutate({ token, client, uid });
    }
  }, [mutate, token, client, uid]);

  useEffect(() => {
    if (data?.item?.uid) {
      setUserSession({ email: data.item.uid, client: data.item.client, authToken: data.item.token });
    } else if (data?.errors && data?.errors?.length > 0) {
      setUserSession(undefined);
      setError(genericErrorMessage);
    }
  }, [reportToGlobal, setUserSession, data]);

  useEffect(() => {
    if (attendeeCategoryId) {
      setAttendeeCategoryId(attendeeCategoryId);
    }
  }, [attendeeCategoryId, setAttendeeCategoryId]);

  return (
    <RegisterLayout
      // header={(groupRegistrationEnabled && !!(email ?? userSession?.email)) ? <GroupHeader /> : null}
      header={null}
      enableHeader={false}
    >
      {error && (
        <UiStack>
          <BaseMessageBarError borderRadius={uiStyles.borderRadius}>
            {error}
          </BaseMessageBarError>
        </UiStack>
      )}
      <div id='test'></div>
      <EmailLayout
        onShowPasscode={setEmail}
        groupRegistrationEnabled={groupRegistrationEnabled}
        toggleGroupRego={toggleGroupRego}
        isGroupRego={groupRegistrationEnabled}
      />
      <LoginDrawer
        groupRegistrationEnabled={groupRegistrationEnabled}
        isOpen={!!email}
        email={email}
        onClose={() => { return setEmail(undefined); }}
      />

    </RegisterLayout>
  );
};

export default EmailPage;
